import { useSpotPrices } from '@e2m-customer-portal/hooks';
import { getMonthNumber } from '@e2m-customer-portal/shared';
import { FormattedValue } from '@e2m-customer-portal/ui';
import { Tooltip } from '@mui/material';
import { getMonth, Interval, startOfMonth, toDate } from 'date-fns';
import { memo, ReactElement, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from '../../../../assets/icons';
import { TitleMetaItem } from './TitleMetaItem';

export interface SpotPriceMetaItemProps {
  interval: Interval;
  isMobile: boolean;
  onDark?: boolean;
}

function SpotPriceMetaContentMemo({
  interval,
  isMobile,
  onDark,
}: Readonly<SpotPriceMetaItemProps>): ReactElement<SpotPriceMetaItemProps> {
  const [t] = useTranslation();
  const { data: prices } = useSpotPrices({ interval: { start: toDate(interval.start), end: toDate(interval.end) } });

  if (prices?.monthlyMarketValues.length)
    return (
      <>
        {prices.monthlyMarketValues.map((value, index) => {
          const month = getMonthNumber(value.month);

          return (
            // if the interval is between months show only the monthly market value of the previous month in mobile
            ((isMobile && index === 0) || !isMobile) && (
              <TitleMetaItem
                key={value.month + value.year}
                onDark={onDark}
                label={
                  <span className="inline-flex items-center gap-1">
                    {t(
                      value.isProvisional
                        ? 'SPOT_PRICES_MONTHLY_MARKET_VALUE_PROVISIONAL'
                        : 'SPOT_PRICES_MONTHLY_MARKET_VALUE',
                      {
                        month: toDate(startOfMonth(new Date()).setMonth(month)),
                      },
                    )}
                    {!isMobile && index === prices.monthlyMarketValues.length - 1 && (
                      <Tooltip
                        title={t('SPOT_PRICES_MONTHLY_MARKET_VALUE_PROVISIONAL_INFO')}
                        PopperProps={{ className: 'whitespace-pre-line' }}
                      >
                        <InfoIcon className="size-4 text-green-200" />
                      </Tooltip>
                    )}
                  </span>
                }
                value={
                  <FormattedValue
                    variant="raw"
                    fractionDigits={0}
                    value={{
                      value: value.mean,
                      unit: '€/MWh',
                    }}
                  />
                }
              />
            )
          );
        })}
      </>
    );
  else
    return (
      <TitleMetaItem
        onDark={onDark}
        label={t('SPOT_PRICES_MONTHLY_MARKET_VALUE', {
          month: toDate(startOfMonth(new Date()).setMonth(getMonth(interval.end))),
        })}
        value={<FormattedValue variant="raw" fractionDigits={0} value={t('SHARED_UNKNOWN')} />}
      />
    );
}

export const SpotPriceMetaContent = memo(SpotPriceMetaContentMemo);

function SpotPriceMetaItemMemo({
  interval,
  isMobile,
  onDark,
}: Readonly<SpotPriceMetaItemProps>): ReactElement<SpotPriceMetaItemProps> {
  const [t] = useTranslation();

  // Use the month that would be displayed as the second month as fallback,
  // so the element doesn’t jump after loading has finished
  const month = toDate(startOfMonth(new Date()).setMonth(getMonth(interval.end)));

  return (
    <ErrorBoundary
      fallback={
        <TitleMetaItem
          onDark={onDark}
          label={t('SPOT_PRICES_MONTHLY_MARKET_VALUE', {
            month,
          })}
          value={t('SHARED_UNKNOWN')}
        />
      }
    >
      <Suspense
        fallback={
          <TitleMetaItem
            onDark={onDark}
            label={t('SPOT_PRICES_MONTHLY_MARKET_VALUE', {
              month,
            })}
            value={t('SHARED_UNKNOWN')}
          />
        }
      >
        <SpotPriceMetaContent interval={interval} isMobile={isMobile} onDark={onDark} />
      </Suspense>
    </ErrorBoundary>
  );
}

export const SpotPriceMetaItem = memo(SpotPriceMetaItemMemo);
